/**
 * PrerequisitesPanelDialog.tsx
 * Phase 5, Item 1 — admin UI for managing TestPrerequisite links.
 *
 * Opened via "Manage Prerequisites" button in the TestCatalogPage toolbar.
 * Shows a searchable list of all test catalog items; clicking a row expands
 * its prerequisites and allows adding/deleting them.
 *
 * NOTE: the per-prerequisite MinSkillLevel gate was dropped from the UI in the
 * RBAC audit (2026-07-10, decision D4). It was never enforced — the scheduling
 * skill gate reads TaskType.SkillKey + the L3+ mentor rule, not this column —
 * so the "[L3]" chip was misleading. The column is retained in the DB but is no
 * longer surfaced or writable here; a prerequisite is now simply "X requires Y".
 *
 * All API calls use the scheduleTaxonomy service (apiClient-backed).
 * Colors come from theme.schedule.* — no hex literals.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import {
  Search,
  Add,
  Delete,
  ExpandMore,
  ExpandLess,
  Close,
  Save,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import type { SelectChangeEvent } from '@mui/material';
import {
  fetchTestPrerequisites,
  createTestPrerequisite,
  deleteTestPrerequisite,
} from '../../../services/scheduleTaxonomy';
import type { TestPrerequisiteDetail } from '../../../services/scheduleTaxonomy';
import type { TestCatalogItem } from '../../../types/testCatalogItem';

interface Props {
  open: boolean;
  onClose: () => void;
  allItems: TestCatalogItem[];
  /**
   * PR B (test-catalog-modernize): when set, the dialog opens focused on this
   * item — search pre-filled with its name and its row pre-expanded (the
   * detail pane's per-test "Prerequisites" button). Null = classic full list.
   */
  focusItemKey?: string | null;
}

interface AddPrereqState {
  forItemKey: string;
  requiredItemKey: string;
}

export const PrerequisitesPanelDialog: React.FC<Props> = ({
  open,
  onClose,
  allItems,
  focusItemKey = null,
}) => {
  const theme = useTheme();

  const [searchQuery, setSearchQuery] = useState('');
  const [allPrereqs, setAllPrereqs] = useState<TestPrerequisiteDetail[]>([]);
  const [loadingPrereqs, setLoadingPrereqs] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [addState, setAddState] = useState<AddPrereqState | null>(null);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const loadPrereqs = useCallback(async () => {
    setLoadingPrereqs(true);
    try {
      const data = await fetchTestPrerequisites();
      setAllPrereqs(data);
    } catch {
      setErrorMsg('Failed to load prerequisites');
    } finally {
      setLoadingPrereqs(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadPrereqs();
      // Focused open (per-test button): pre-search + pre-expand the item.
      const focusItem = focusItemKey
        ? allItems.find((i) => i.TestCatalogItemKey === focusItemKey) ?? null
        : null;
      setSearchQuery(focusItem?.TestName ?? '');
      setExpandedKeys(focusItem ? new Set([focusItem.TestCatalogItemKey]) : new Set());
      setAddState(null);
      setErrorMsg(null);
    }
    // allItems is intentionally not a dependency — re-running on list refresh
    // would clobber the user's search text mid-session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, focusItemKey, loadPrereqs]);

  const filteredItems = allItems.filter((item) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      item.TestName.toLowerCase().includes(q) ||
      (item.Category ?? '').toLowerCase().includes(q)
    );
  });

  const prereqsFor = (itemKey: string): TestPrerequisiteDetail[] =>
    allPrereqs.filter((p) => p.TestCatalogItemKey === itemKey);

  const toggleExpand = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleStartAdd = (itemKey: string) => {
    setAddState({ forItemKey: itemKey, requiredItemKey: '' });
    setErrorMsg(null);
  };

  const handleCancelForm = () => {
    setAddState(null);
    setErrorMsg(null);
  };

  const handleSaveAdd = async () => {
    if (!addState || !addState.requiredItemKey) {
      setErrorMsg('Please select the required test.');
      return;
    }
    setSaving(true);
    setErrorMsg(null);
    try {
      await createTestPrerequisite({
        TestCatalogItemKey: addState.forItemKey,
        PrerequisiteItemKey: addState.requiredItemKey,
      });
      setAddState(null);
      await loadPrereqs();
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ??
        (err instanceof Error ? err.message : 'Failed to create prerequisite');
      setErrorMsg(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (prereqKey: string) => {
    setSaving(true);
    setErrorMsg(null);
    try {
      await deleteTestPrerequisite(prereqKey);
      await loadPrereqs();
    } catch {
      setErrorMsg('Failed to delete prerequisite');
    } finally {
      setSaving(false);
    }
  };

  // Items that could be prerequisites for the selected test (exclude itself)
  const candidatesFor = (itemKey: string): TestCatalogItem[] =>
    allItems.filter((i) => i.TestCatalogItemKey !== itemKey);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Manage Test Prerequisites
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {errorMsg && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setErrorMsg(null)}>
            {errorMsg}
          </Alert>
        )}

        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search tests by name or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loadingPrereqs ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <List dense disablePadding>
            {filteredItems.map((item, idx) => {
              const isExpanded = expandedKeys.has(item.TestCatalogItemKey);
              const prereqs = prereqsFor(item.TestCatalogItemKey);
              const isAddingHere =
                addState?.forItemKey === item.TestCatalogItemKey;

              return (
                <React.Fragment key={item.TestCatalogItemKey}>
                  {idx > 0 && <Divider />}

                  {/* Test row */}
                  <ListItem
                    button
                    onClick={() => toggleExpand(item.TestCatalogItemKey)}
                    sx={{
                      bgcolor: isExpanded
                        ? theme.table.stripeBg
                        : 'transparent',
                      '&:hover': { bgcolor: theme.table.hoverBg },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.TestName}
                          </Typography>
                          {item.Category && (
                            <Chip
                              label={item.Category}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 10 }}
                            />
                          )}
                          {prereqs.length > 0 && (
                            <Chip
                              label={`${prereqs.length} prereq${prereqs.length !== 1 ? 's' : ''}`}
                              size="small"
                              sx={{
                                bgcolor: theme.schedule.committed,
                                color: '#fff',
                                fontSize: 10,
                              }}
                            />
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartAdd(item.TestCatalogItemKey);
                          if (!isExpanded) toggleExpand(item.TestCatalogItemKey);
                        }}
                        title="Add prerequisite"
                      >
                        <Add fontSize="small" />
                      </IconButton>
                      {isExpanded ? (
                        <ExpandLess fontSize="small" />
                      ) : (
                        <ExpandMore fontSize="small" />
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>

                  {/* Expanded prerequisites + add form */}
                  <Collapse in={isExpanded} unmountOnExit>
                    <Box
                      sx={{
                        bgcolor: theme.table.stripeBg,
                        borderLeft: `4px solid ${theme.schedule.committed}`,
                        ml: 2,
                        mr: 1,
                        mb: 1,
                        px: 2,
                        py: 1,
                      }}
                    >
                      {prereqs.length === 0 && !isAddingHere && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontStyle: 'italic' }}
                        >
                          No prerequisites defined. Click + to add one.
                        </Typography>
                      )}

                      {/* Existing prerequisites */}
                      {prereqs.map((prereq) => (
                        <Box
                          key={prereq.TestPrerequisiteKey}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.5,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography variant="body2">
                            {prereq.PrerequisiteTestName}
                          </Typography>
                          {prereq.PrerequisiteCategory && (
                            <Chip
                              label={prereq.PrerequisiteCategory}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 10 }}
                            />
                          )}
                          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleDelete(prereq.TestPrerequisiteKey)
                              }
                              disabled={saving}
                              title="Remove prerequisite"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}

                      {/* Add new prerequisite form */}
                      {isAddingHere && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            pt: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>Required Test</InputLabel>
                            <Select
                              label="Required Test"
                              value={addState.requiredItemKey}
                              onChange={(e: SelectChangeEvent) =>
                                setAddState((s) =>
                                  s ? { ...s, requiredItemKey: e.target.value } : s,
                                )
                              }
                            >
                              {candidatesFor(item.TestCatalogItemKey).map((c) => (
                                <MenuItem key={c.TestCatalogItemKey} value={c.TestCatalogItemKey}>
                                  {c.TestName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleSaveAdd}
                            disabled={saving || !addState.requiredItemKey}
                            title="Add prerequisite"
                          >
                            <Save fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={handleCancelForm} title="Cancel">
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
